import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminUserApiService } from '@cnt-admin/data-access';
import { IUser } from '@curbnturf/entities';
import { IonButton, IonItem, IonLabel, IonList, IonSearchbar } from '@ionic/angular/standalone';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';

/**
 * Server-side user search (GET /user/paginated) for picking a listing/boondock
 * owner. Replaces loading every user through @curbnturf/user's UserFacade.
 */
@Component({
  selector: 'admin-user-picker',
  standalone: true,
  imports: [IonButton, IonItem, IonLabel, IonList, IonSearchbar],
  template: `
    @if (selected) {
      <ion-item lines="none">
        <ion-label>
          {{ userText(selected) }}
        </ion-label>
        <ion-button slot="end" fill="clear" size="small" (click)="clear()">Change</ion-button>
      </ion-item>
    } @else {
      <ion-searchbar
        [placeholder]="placeholder"
        [debounce]="0"
        (ionInput)="terms$.next($any($event).detail.value ?? '')"
      ></ion-searchbar>
      @if (results.length) {
        <ion-list class="results">
          @for (user of results; track user.id) {
            <ion-item button detail="false" (click)="pick(user)">
              <ion-label>
                {{ userText(user) }}
                <p>{{ user.email }}</p>
              </ion-label>
            </ion-item>
          }
        </ion-list>
      }
    }
  `,
  styles: [
    `
      .results {
        max-height: 280px;
        overflow-y: auto;
        margin-top: 6px;
        padding: 4px;
        border: 1px solid var(--cnt-line);
        border-radius: 0.75rem;
        background: #fff;
      }
      .results ion-item {
        --border-radius: 0.5rem;
        --background-hover: var(--cnt-cream);
        --background-hover-opacity: 1;
      }
    `,
  ],
})
export class UserPickerComponent implements OnInit {
  private readonly users = inject(AdminUserApiService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() selected?: IUser;
  @Input() placeholder = 'Search users by name, email or id';
  @Output() readonly selectedChange = new EventEmitter<IUser | undefined>();

  readonly terms$ = new Subject<string>();
  results: IUser[] = [];

  ngOnInit(): void {
    this.terms$
      .pipe(
        debounceTime(300),
        map((t) => t.trim()),
        distinctUntilChanged(),
        switchMap((term) =>
          term.length < 2
            ? of<IUser[]>([])
            : this.users.search(term, 1, 20).pipe(
                map((page) => page.users),
                catchError(() => of<IUser[]>([])),
              ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((users) => (this.results = users));
  }

  pick(user: IUser): void {
    this.selected = user;
    this.results = [];
    this.selectedChange.emit(user);
  }

  clear(): void {
    this.selected = undefined;
    this.selectedChange.emit(undefined);
  }

  userText(user: IUser): string {
    return `${user.firstName ?? ''} ${user.lastName ?? ''} (${user.id})`.trim();
  }
}
